import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { BooleanReportParameter } from '@ndt/report-output/shared/models/boolean-report-parameter';
import { ColumnSortReportParameter } from '@ndt/report-output/shared/models/column-sort-report-parameter';
import { SelectedAccountReportParameter } from '@ndt/report-output/shared/models/selected-account-report-parameter';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import { EntitiesToIncludeParameter } from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import {
   CallParkingTileReportParametersInput,
   CallQueueExtensionsToIncludeReportParameterInput,
   ClockTileReportParametersInput,
   PhoneQueuesTileReportParametersInput,
   ReportParameters,
   Tiles,
   TileViewModes,
   WaitTimesTileReportParametersInput,
} from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { DashboardTileUnionType } from '../../dashboard/models/dashboard-tile.model';
import { ExecuteTilePostModel } from '../models/execute-tile-post.model';
import { TileDataUnion } from '../models/tile-data.model';
import { runCallParking } from '../views/call-parking/queries/call-parking-tile.graphql';
import { runClock } from '../views/clock/queries/clock-tile.graphql';
import { TimeRangeReportParameter } from '../views/ndt-wait-times-graph-tile/models/wait-times-tile';
import { runNdtWaitTimesGraph } from '../views/ndt-wait-times-graph-tile/queries/ndt-wait-times-graphql';
import { runPhoneQueues } from '../views/phone-queues/queries/phone-queues-tile.graphql';
import { runQueueMembers } from '../views/queue-members/queries/queue-members-tile.graphql';

@Injectable({ providedIn: 'root' })
export class TileApiService {
   constructor(private apiService: LcsApiService, private httpService: HttpClient) {}

   getTile(
      dashboardTile: DashboardTileUnionType,
      viewMode: TileViewModes,
      pageNumber: number | null = null,
      pageSize: number | null = null
   ): Observable<ExecutionResult<TileDataUnion>> {
      const { params, query } = this.getTileLoadInfo(dashboardTile, viewMode, pageNumber, pageSize);

      return this.apiService.query({
         query,
         variables: { params },
      });
   }

   getTileLoadInfo(
      dashboardTile: DashboardTileUnionType | undefined,
      viewMode: TileViewModes | undefined,
      pageNumber: number | null = null,
      pageSize: number | null = null
   ): { params: ExecuteTilePostModel; query: string } {
      if (!dashboardTile || !viewMode) throw new Error('Not a dashboardTile or viewMode');

      switch (dashboardTile.tile.tileId) {
         case Tiles.Clock:
            return { params: this.getClockTileExecutePostModel(dashboardTile, viewMode), query: runClock };
         case Tiles.CallParking:
            return {
               params: this.getParkedCallTileExecutePostModel(dashboardTile, viewMode, pageNumber, pageSize),
               query: runCallParking,
            };
         case Tiles.QueueMembers:
            return {
               params: this.getQueueMembersTileExecutePostModel(dashboardTile, viewMode),
               query: runQueueMembers,
            };
         case Tiles.PhoneQueues:
            return {
               params: this.getPhoneQueuesTileExecutePostModel(dashboardTile, viewMode),
               query: runPhoneQueues,
            };
         case Tiles.NDTWaitTimesGraph:
            return {
               params: this.getNDTWaitTimesGraphTileExecutePostModel(dashboardTile, viewMode),
               query: runNdtWaitTimesGraph,
            };
         default:
            throw new Error(`${dashboardTile.tile.tileId} is not a valid tile id`);
      }
   }

   saveTile(
      dashboardTileId: number,
      reportParameters: any,
      tileId: Tiles,
      dashboardId: number
   ): Observable<TileDataUnion> {
      const tileLayoutConfiguration = reportParameters.tileLayoutConfiguration;
      const tileIdUpdate = tileLayoutConfiguration.fields.includes('tileSetType');

      const url = this.getTileSaveUrl(tileIdUpdate ? tileLayoutConfiguration.tileSetType.tile.tileId : tileId);
      const body = {
         parameters: reportParameters,
         dashboardTileId,
         dashboardId,
      };
      return this.httpService.post<TileDataUnion>(url, body);
   }

   deleteTile(dashboardTileId: number): Observable<void> {
      const url = `${this.apiService.apiUrl}/DashboardTiles/${dashboardTileId}`;
      return this.httpService.delete<void>(url);
   }

   private getClockTileExecutePostModel(
      dashboardTile: DashboardTileUnionType,
      tileViewMode: TileViewModes
   ): ExecuteTilePostModel {
      const showDateParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.ShowDate;
      }) as BooleanReportParameter;
      const timeZoneParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.TimeZone;
      }) as TimeZoneReportParameter;

      const parameters: ClockTileReportParametersInput = {
         showDate: {
            isAskParameter: false,
            sortOrder: 1,
            //TODO: customize clockTile ticket
            value: showDateParameter.value,
            fields: [],
         },
         timeZone: {
            value: timeZoneParameter?.value ?? null,
            fields: [],
         },
         fields: [],
      };

      return {
         parameters,
         tileId: dashboardTile.tile.tileId,
         tileViewMode,
         dashboardTileId: dashboardTile.dashboardTileId,
         fields: [],
      };
   }

   private getParkedCallTileExecutePostModel(
      dashboardTile: DashboardTileUnionType,
      tileViewMode: TileViewModes,
      pageNumber: number | null = null,
      pageSize: number | null = null
   ): ExecuteTilePostModel {
      const selectedAccountParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.SelectedAccount;
      }) as SelectedAccountReportParameter;

      const parameters: CallParkingTileReportParametersInput = {
         selectedAccount: {
            value: selectedAccountParameter?.value ?? null,
            name: selectedAccountParameter?.name,
            fields: [],
         },
         fields: [],
      };

      return {
         parameters,
         tileId: dashboardTile.tile.tileId,
         tileViewMode,
         dashboardTileId: dashboardTile.dashboardTileId,
         pageSize,
         fields: [],
      };
   }

   private getNDTWaitTimesGraphTileExecutePostModel(
      dashboardTile: DashboardTileUnionType,
      tileViewMode: TileViewModes
   ): ExecuteTilePostModel {
      const timeRangeParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.TimeRange;
      }) as TimeRangeReportParameter | undefined;

      const queuesToIncludeParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.CallQueueExtensionsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const selectedAccountParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.SelectedAccount;
      }) as SelectedAccountReportParameter;

      const aggregateQueuesParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.AggregateQueues;
      }) as BooleanReportParameter;
      const parameters: WaitTimesTileReportParametersInput = {
         selectedAccount: {
            value: selectedAccountParameter?.value ?? null,
            name: selectedAccountParameter?.name,
            fields: [],
         },
         callQueues: {
            all: queuesToIncludeParameter?.all ?? false,
            values: queuesToIncludeParameter?.values ?? [],
            isAskParameter: false,
            fields: [],
         },
         timeRange: {
            startDate: timeRangeParameter?.startDate,
            endDate: timeRangeParameter?.endDate,
            sortOrder: 2,
            fields: [],
         },
         aggregateQueues: {
            isAskParameter: false,
            sortOrder: 4,
            value: aggregateQueuesParameter?.value ?? false,
            fields: [],
         },
         fields: [],
      };

      return {
         parameters,
         tileId: dashboardTile.tile.tileId,
         tileViewMode,
         dashboardTileId: dashboardTile.dashboardTileId,
         fields: [],
      };
   }

   private getQueueMembersTileExecutePostModel(
      dashboardTile: DashboardTileUnionType,
      tileViewMode: TileViewModes
   ): ExecuteTilePostModel {
      const selectedAccountParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.SelectedAccount;
      }) as SelectedAccountReportParameter;

      const callQueueExtensions = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.CallQueueExtensionsToInclude;
      }) as CallQueueExtensionsToIncludeReportParameterInput;

      const columnSortParamter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.ColumnSort;
      }) as ColumnSortReportParameter;

      const parameters: any = {
         selectedAccount: {
            value: selectedAccountParameter?.value ?? null,
            name: selectedAccountParameter?.name,
            fields: [],
         },
         callQueueExtensions: callQueueExtensions
            ? {
                 accountId: callQueueExtensions.accountId ?? 0,
                 all: !!callQueueExtensions.all,
                 values: callQueueExtensions.values ?? [],
                 names: callQueueExtensions.names ?? [],
                 fields: [],
              }
            : undefined,
         columnSort: {
            value: columnSortParamter ? `${columnSortParamter?.value}` : '',
            fields: [],
         },
         fields: [],
      };

      return {
         parameters,
         tileId: dashboardTile.tile.tileId as any,
         tileViewMode,
         dashboardTileId: dashboardTile.dashboardTileId,
         fields: [],
      } as unknown as ExecuteTilePostModel;
   }

   private getPhoneQueuesTileExecutePostModel(
      dashboardTile: DashboardTileUnionType,
      tileViewMode: TileViewModes
   ): ExecuteTilePostModel {
      const selectedAccountParameter = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.SelectedAccount;
      }) as SelectedAccountReportParameter;

      const callQueueExtensions = dashboardTile.dashboardTileParameters.find((dtp) => {
         return dtp.parameterType === ReportParameters.CallQueueExtensionsToInclude;
      }) as CallQueueExtensionsToIncludeReportParameterInput;

      const parameters: PhoneQueuesTileReportParametersInput = {
         selectedAccount: {
            value: selectedAccountParameter?.value ?? null,
            name: selectedAccountParameter?.name,
            fields: [],
         },
         callQueueExtensions: callQueueExtensions
            ? {
                 accountId: callQueueExtensions.accountId ?? 0,
                 all: !!callQueueExtensions.all,
                 values: callQueueExtensions.values ?? [],
                 names: callQueueExtensions.names ?? [],
                 fields: [],
              }
            : undefined,
         fields: [],
      };

      return {
         parameters,
         tileId: dashboardTile.tile.tileId,
         tileViewMode,
         dashboardTileId: dashboardTile.dashboardTileId,
         fields: [],
      } as ExecuteTilePostModel;
   }

   private getTileSaveUrl(tileId: Tiles): string {
      let url = `${this.apiService.apiUrl}/DashboardTiles/`;
      switch (tileId) {
         case Tiles.Clock:
            url += 'SaveClock';
            break;
         case Tiles.CallParking:
            url += 'SaveCallParking';
            break;
         case Tiles.QueueMembers:
            url += 'SaveQueueMembers';
            break;
         case Tiles.PhoneQueues:
            url += 'SavePhoneQueues';
            break;
         case Tiles.NDTWaitTimesGraph:
            url += 'SaveWaitTime';
            break;
         default:
            throw new Error('Not a valid tile id');
      }
      return url;
   }
}
